import React from "react";
import { useViewerCopy } from "../../utils/viewerLanguage";
import { isSenderContext } from "../../utils/cookieConsentUtils";
import { blockedContentCopy, BLOCKED_ICON_MARKUP } from "../../utils/blockedContentCopy";

const VIDEO_DOMAINS = /youtube\.com|youtu\.be|vimeo\.com|wistia\.(com|net)|dailymotion\.com|loom\.com|vidyard\.com/i;
const AUDIO_DOMAINS = /soundcloud\.com|spotify\.com/i;

export const detectContentType = (element) => {
  try {
    const url = element?.value?.video_link || "";
    if (VIDEO_DOMAINS.test(url)) return "video";
    if (AUDIO_DOMAINS.test(url)) return "audio";
    const type = element?.element_type_name?.toLowerCase() || "";
    if (type === "video") return "video";
    if (type === "audio") return "audio";
  } catch (err) { console.warn(err); }
  return "content";
};

const CookieBlockedMessage = ({ style = {}, onClick, dimensions, contentType = "content", large = false, sourceUrl }) => {
  const viewerLanguage = useViewerCopy();
  const consent = viewerLanguage.copy?.consent;
  const sender = isSenderContext();
  const copy = blockedContentCopy(sender ? null : consent, contentType, sourceUrl);
  const kind = copy.kind;
  const title = copy.title;
  const settingsLabel = copy.button;
  const showSteps = !sender;
  const hint = sender ? `Third-party content is blocked. Accept all cookies to preview this ${kind}.` : copy.hint;
  const steps = copy.steps;
  const handleOpenCookieSettings = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("openCookieConsentModal"));
    }
  };

  // Determine layout mode from the element's physical dimensions.
  // In portrait on small phones the element area can be as little as 150×70px,
  // which isn't tall enough to show icon + title + subtitle + button without clipping.
  const containerH = parseFloat(style.height) || 0;
  const containerW = parseFloat(style.width)  || 0;

  // Full layout: element is large enough to show everything (landscape / tablet / big element).
  // Compact layout: limited height — drop description text and shrink icon.
  // Mini layout: very small element — icon hidden, one-line title + tiny button only.
  const isMini    = !large && (containerH < 80  || containerW < 130);
  const isCompact = !large && !isMini && (containerH < 140 || containerW < 200);

  const iconSize     = large ? 44 : isMini ? 0  : isCompact ? 20 : 28;
  const titleSize    = large ? "20px" : isMini ? "9px" : isCompact ? "10px" : "11px";
  const hintSize     = large ? "14px" : "10px";
  const btnFontSize  = large ? "14px" : isMini ? "8px" : isCompact ? "9px"  : "10px";
  const btnPadding   = large ? "8px 18px" : isMini ? "3px 7px" : isCompact ? "3px 8px" : "4px 10px";
  const outerPadding = large ? "24px" : isMini ? "4px" : isCompact ? "8px 6px" : "12px 8px";

  return (
    <div
      style={{
        position: "relative",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f0f4f8",
        border: "1.5px solid #c8d6e5",
        borderRadius: "6px",
        padding: outerPadding,
        textAlign: "center",
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        zIndex: 10,
        ...style,
      }}
      onClick={onClick}
      title={hint}
      dir={consent ? viewerLanguage.dir : undefined}
      lang={consent ? viewerLanguage.locale : undefined}
    >
      {iconSize > 0 && (
        <svg
          width={iconSize}
          height={iconSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#5a7fa8"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          xmlns="http://www.w3.org/2000/svg"
          style={{ opacity: 0.55, flexShrink: 0, marginBottom: large ? "12px" : isCompact ? "4px" : "6px" }}
          dangerouslySetInnerHTML={{ __html: BLOCKED_ICON_MARKUP[kind] }}
        />
      )}

      <div
        style={{
          fontWeight: "700",
          fontSize: titleSize,
          color: "#2c3e50",
          marginBottom: large ? "8px" : isMini ? "3px" : "4px",
          lineHeight: "1.3",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </div>

      {!isMini && !isCompact && (
        <div
          style={{
            fontSize: hintSize,
            color: "#666",
            lineHeight: "1.4",
            marginBottom: large ? "14px" : "8px",
            maxWidth: large ? "320px" : "180px",
          }}
        >
          {hint}
        </div>
      )}

      {showSteps && !isMini && !isCompact && (
        <div
          style={{
            alignSelf: "center",
            textAlign: "start",
            fontSize: hintSize,
            color: "#444",
            lineHeight: "1.5",
            backgroundColor: "#fff",
            borderRadius: "4px",
            padding: large ? "10px 16px" : "6px 10px",
            marginBottom: large ? "14px" : "8px",
            maxWidth: large ? "320px" : "180px",
          }}
        >
          <div>{copy.stepsTitle}</div>
          <ol style={{ margin: "4px 0 0", paddingInlineStart: large ? "22px" : "16px" }}>
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>
      )}

      <button
        type="button"
        onClick={handleOpenCookieSettings}
        style={{
          background: "#294561",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          width: "auto",
          minWidth: 0,
          flex: "0 0 auto",
          alignSelf: showSteps && !isMini && !isCompact ? "flex-end" : "center",
          textTransform: "none",
          padding: btnPadding,
          fontSize: btnFontSize,
          fontWeight: "600",
          cursor: "pointer",
          whiteSpace: "nowrap",
          marginTop: isCompact ? "2px" : 0,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#1d3348")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "#294561")}
      >
        {settingsLabel}
      </button>
    </div>
  );
};

export default CookieBlockedMessage;
